import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1548123378-bde4eca81d2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function AuthPage() {
  const { login, register, loginGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [artistName, setArtistName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, artistName);
      toast.success("Welcome to NEXT UP 🎧");
      navigate("/feed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0E] text-white">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-transparent to-[#0A0A0E]/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display font-black text-4xl leading-tight">Release. Connect.<br /><span className="text-gradient">Blow up.</span></h2>
          <p className="mt-3 text-zinc-300 max-w-sm">The members-only network built for rising music artists.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/40 glow-emerald"><Radio className="w-5 h-5 text-emerald-400" /></span>
            <span className="font-display font-extrabold text-lg">NEXT<span className="text-emerald-400">UP</span></span>
          </Link>

          <h1 className="font-display font-extrabold text-3xl">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="mt-2 text-zinc-400 text-sm">{mode === "login" ? "Sign in to your artist account." : "Start your journey on NEXT UP."}</p>

          <button data-testid="google-auth-btn" onClick={loginGoogle}
            className="mt-8 w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-white text-black font-semibold hover:bg-zinc-200 transition-colors">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-zinc-600 text-xs font-mono-nu uppercase tracking-widest">
            <span className="flex-1 h-px bg-white/10" /> or <span className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input data-testid="auth-artist-name" value={artistName} onChange={(e) => setArtistName(e.target.value)} required
                placeholder="Artist / stage name"
                className="w-full bg-[#181824] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-emerald-500/50 transition-colors" />
            )}
            <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              placeholder="Email address"
              className="w-full bg-[#181824] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-emerald-500/50 transition-colors" />
            <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              placeholder="Password"
              className="w-full bg-[#181824] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-emerald-500/50 transition-colors" />
            <button type="submit" disabled={busy} data-testid="auth-submit"
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors disabled:opacity-50">
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-zinc-400">
            {mode === "login" ? "New to NEXT UP?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-emerald-400 font-semibold hover:underline">
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
